import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;
const CHUNK_SIZE = 1024 * 1024;

// Chunked upload for large files (book PDFs / audiobooks / trailers) — 1MB
// parts with retry, then background assembly + status polling on the server.
// onProgress(percent, statusText) is optional.
export const uploadFileInChunks = async (file, { fileType, bookId, token, onProgress, label }) => {
  const report = (pct, text) => onProgress?.(pct, text);
  const name = label || `${fileType} file`;
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
  report(0, `Initializing ${name} upload (${totalChunks} parts)...`);
  const initRes = await axios.post(`${API}/upload/init`, null, {
    headers: { Authorization: `Bearer ${token}` },
    params: {
      filename: file.name,
      file_size: file.size,
      file_type: fileType,
      content_type: file.type || 'application/octet-stream',
      total_chunks: totalChunks,
      book_id: bookId,
    },
    timeout: 30000,
  });
  const uploadId = initRes.data.upload_id;
  for (let i = 0; i < totalChunks; i++) {
    const chunk = file.slice(i * CHUNK_SIZE, Math.min((i + 1) * CHUNK_SIZE, file.size));
    let ok = false;
    for (let attempt = 1; attempt <= 3 && !ok; attempt++) {
      try {
        const fd = new FormData();
        fd.append('chunk', chunk, `chunk_${i}`);
        await axios.post(`${API}/upload/chunk/${uploadId}/${i}`, fd, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
          timeout: 120000,
        });
        ok = true;
      } catch (err) {
        if (attempt === 3) throw err;
        await new Promise((r) => setTimeout(r, 2000 * attempt));
      }
    }
    report(Math.round(((i + 1) / totalChunks) * 90), `Uploading ${name} part ${i + 1} of ${totalChunks}...`);
  }
  report(90, `Finalizing ${name}...`);
  try {
    await axios.post(`${API}/upload/complete/${uploadId}`, null, {
      headers: { Authorization: `Bearer ${token}` },
      timeout: 30000,
    });
  } catch (e) {
    if (e.code !== 'ECONNABORTED' && !e.message?.includes('timeout')) throw e;
  }
  for (let attempts = 0; attempts < 100; attempts++) {
    await new Promise((r) => setTimeout(r, 3000));
    const st = await axios.get(`${API}/upload/status/${uploadId}`, {
      headers: { Authorization: `Bearer ${token}` },
      timeout: 15000,
    });
    if (st.data.status === 'completed') return st.data.file_url;
    if (st.data.status === 'failed') throw new Error(st.data.error || 'Upload processing failed');
    report(92 + Math.min(7, Math.floor(attempts / 5)), `Processing ${name} on server...`);
  }
  throw new Error(`${name} processing timed out`);
};
